import { useState, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Collapse from '@mui/material/Collapse';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import EmptyState from '../components/common/EmptyState';
import { useGetBookingQuery, useCancelBookingMutation } from '../store/bookingsApi';
import { useUI } from '../context/UIContext';
import { ROUTES, buildRoute } from '../constants/routes';

const CANCEL_HOURS = 3;
const REASONS = [
  'Changed my mind',
  'Wrong slot picked',
  'Sample already collected elsewhere',
  'Health emergency',
  "Trustian didn't arrive",
  'Other',
];

// "6:00 AM" → { h: 6, m: 0 }
const parseSlot = (slot) => {
  const m = String(slot || '').trim().match(/^(\d{1,2}):(\d{2})\s*(AM|PM)?$/i);
  if (!m) return { h: 0, m: 0 };
  let h = Number(m[1]);
  const min = Number(m[2]);
  const ampm = (m[3] || '').toUpperCase();
  if (ampm === 'PM' && h < 12) h += 12;
  if (ampm === 'AM' && h === 12) h = 0;
  return { h, m: min };
};

const scheduledDateTime = (sched) => {
  if (!sched?.date) return null;
  const { h, m } = parseSlot(sched.slot);
  const [y, mo, d] = String(sched.date).split('-').map(Number);
  if (!y || !mo || !d) return null;
  return new Date(y, mo - 1, d, h, m);
};

const formatHours = (hrs) => {
  if (hrs <= 0) return 'past';
  const h = Math.floor(hrs);
  const m = Math.round((hrs - h) * 60);
  if (h === 0) return `${m}m`;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
};

const CancelBooking = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showToast } = useUI();

  const { data, isLoading, isError } = useGetBookingQuery(id, { skip: !id });
  const booking = data?.booking;

  const [cancelBooking, { isLoading: cancelling }] = useCancelBookingMutation();

  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');
  const [needBank, setNeedBank] = useState(false);
  const [bank, setBank] = useState({ account_holder: '', account_number: '', ifsc: '', bank_name: '' });
  const [errMsg, setErrMsg] = useState('');

  const eligibility = useMemo(() => {
    if (!booking) return { ok: false, reason: '', hoursLeft: 0 };
    if (booking.status === 'cancelled') return { ok: false, reason: 'This booking is already cancelled.' };
    if (['collected', 'reported'].includes(booking.status)) {
      return { ok: false, reason: `This booking is already ${booking.status} — too late to cancel.` };
    }
    const sched = scheduledDateTime(booking.schedule);
    if (!sched) return { ok: true, hoursLeft: Infinity };
    const hoursLeft = (sched.getTime() - Date.now()) / 3_600_000;
    if (hoursLeft < CANCEL_HOURS) {
      return {
        ok: false,
        hoursLeft,
        reason: `Cancellations are allowed only up to ${CANCEL_HOURS} hours before your slot. Your slot is in ${formatHours(hoursLeft)}.`,
      };
    }
    return { ok: true, hoursLeft };
  }, [booking]);

  if (isLoading) {
    return (
      <Box sx={{ minHeight: '90vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress sx={{ color: 'var(--sage)' }} />
      </Box>
    );
  }

  if (isError || !booking) {
    return (
      <Box sx={{ py: 10, maxWidth: 560, mx: 'auto', px: 2 }}>
        <EmptyState
          icon="⚠"
          title="Booking not found"
          description={`We couldn't load booking ${id}.`}
          action={<Button variant="contained" onClick={() => navigate(ROUTES.DASHBOARD)}>Back to bookings</Button>}
        />
      </Box>
    );
  }

  const wasPaid = booking.payment?.status === 'paid';
  const total = Number(booking.amounts?.total || 0);

  const onConfirm = async () => {
    setErrMsg('');
    if (!reason) return setErrMsg('Please pick a reason');
    if (needBank) {
      const { account_holder, account_number, ifsc } = bank;
      if (!account_holder.trim() || !account_number.trim() || !ifsc.trim()) {
        return setErrMsg('Please fill in account holder, account number and IFSC');
      }
    }
    try {
      const res = await cancelBooking({
        bookingId: booking.booking_id,
        reason,
        notes: notes.trim() || undefined,
        bank_details: needBank ? bank : undefined,
      }).unwrap();
      const refundStatus = res?.booking?.refund?.status;
      const msg =
        refundStatus === 'auto'
          ? 'Booking cancelled. Your refund has been queued — it should reflect within a few hours.'
          : refundStatus === 'manual'
            ? 'Booking cancelled. Our team will process the refund manually within 2–3 working days.'
            : 'Booking cancelled.';
      showToast(msg);
      navigate(ROUTES.DASHBOARD);
    } catch (err) {
      setErrMsg(err?.data?.message || 'Could not cancel the booking. Please try again.');
    }
  };

  return (
    <Box sx={{ bgcolor: 'var(--cream)', minHeight: '90vh' }}>
      {/* Header */}
      <Box sx={{ bgcolor: 'var(--forest)', color: '#fff', pt: { xs: 10, md: 12 }, pb: 5, px: { xs: 2, md: 4 } }}>
        <Box sx={{ width: '100%', maxWidth: 640, mx: 'auto' }}>
          <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase', color: 'var(--sage-l)', mb: 1.5 }}>
            Cancel booking
          </Typography>
          <Typography className="serif" variant="h2" sx={{ color: '#fff', mb: 0.5, fontSize: { xs: 26, md: 32 } }}>
            {booking.booking_id}
          </Typography>
          <Typography sx={{ fontSize: 14, color: 'rgba(255,255,255,0.55)' }}>
            Slot: {booking.schedule?.date} · {booking.schedule?.slot}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ width: '100%', maxWidth: 640, mx: 'auto', py: 4, px: { xs: 2, md: 4 } }}>
        {/* Summary card */}
        <Box sx={{ bgcolor: '#fff', border: '1px solid var(--border-l)', borderRadius: '16px', p: 3, mb: 3 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 1 }}>
            <Typography sx={{ fontSize: 14, fontWeight: 700, color: 'var(--ink)' }}>
              Tests booked
            </Typography>
            <Typography className="serif" sx={{ fontSize: 22, fontWeight: 700, color: 'var(--forest)' }}>
              ₹{total.toLocaleString()}
            </Typography>
          </Stack>
          <Stack spacing={0.5}>
            {(booking.items || []).map((it) => (
              <Typography key={`${it.item_type}-${it.item_id}`} sx={{ fontSize: 13, color: 'var(--stone)' }}>
                · {it.name}
              </Typography>
            ))}
          </Stack>
          <Box sx={{ mt: 2, pt: 2, borderTop: '1px dashed var(--border-l)', fontSize: 12, color: 'var(--stone)' }}>
            Paid via {(booking.payment?.method || '').toUpperCase()}
            {booking.payment?.gateway_txn_id ? ` · Txn ${booking.payment.gateway_txn_id}` : ''}
            {' · Status '}
            <Box component="span" sx={{ fontWeight: 700, color: wasPaid ? 'var(--sage)' : 'var(--stone)' }}>
              {(booking.payment?.status || '').toUpperCase()}
            </Box>
          </Box>
        </Box>

        {/* Eligibility gate */}
        {!eligibility.ok ? (
          <>
            <Alert severity="warning" sx={{ mb: 2, borderRadius: '12px' }}>
              {eligibility.reason}
            </Alert>
            <Button
              variant="outlined" onClick={() => navigate(buildRoute(ROUTES.TRACK, {}) + `?id=${booking.booking_id}`)}
              sx={{ borderRadius: 100, textTransform: 'none', fontWeight: 600 }}
            >
              ← Back to Track
            </Button>
          </>
        ) : (
          <Box sx={{ bgcolor: '#fff', border: '1px solid var(--border-l)', borderRadius: '16px', p: 3 }}>
            <Typography sx={{ fontSize: 16, fontWeight: 700, color: 'var(--ink)', mb: 0.5 }}>
              Tell us why you're cancelling
            </Typography>
            <Typography sx={{ fontSize: 13, color: 'var(--stone)', mb: 2.5, fontWeight: 300 }}>
              {wasPaid
                ? `Your ₹${total.toLocaleString()} will be refunded to the same payment method you used — it usually reflects within a few hours.`
                : 'No payment was taken — there is nothing to refund.'}
            </Typography>

            {errMsg && <Alert severity="error" sx={{ mb: 2, borderRadius: '10px' }}>{errMsg}</Alert>}

            <Stack spacing={2}>
              <TextField
                select fullWidth label="Reason for cancellation *"
                value={reason} onChange={(e) => setReason(e.target.value)}
              >
                {REASONS.map((r) => <MenuItem key={r} value={r}>{r}</MenuItem>)}
              </TextField>

              <TextField
                fullWidth multiline rows={2}
                label="Anything else we should know? (optional)"
                placeholder="Help us improve — what went wrong?"
                value={notes} onChange={(e) => setNotes(e.target.value)}
              />

              {wasPaid && (
                <>
                  <FormControlLabel
                    control={<Switch checked={needBank} onChange={(e) => setNeedBank(e.target.checked)} />}
                    label={
                      <Typography sx={{ fontSize: 13, color: 'var(--stone)' }}>
                        Refund my original payment method isn't available — pay me to a bank account instead
                      </Typography>
                    }
                  />
                  <Collapse in={needBank}>
                    <Stack spacing={2} sx={{ pt: 0.5 }}>
                      <TextField fullWidth label="Account holder name *"
                        value={bank.account_holder}
                        onChange={(e) => setBank({ ...bank, account_holder: e.target.value })}
                      />
                      <TextField fullWidth label="Account number *"
                        value={bank.account_number}
                        onChange={(e) => setBank({ ...bank, account_number: e.target.value.replace(/\D/g, '').slice(0, 18) })}
                      />
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                        <TextField fullWidth label="IFSC *"
                          value={bank.ifsc}
                          onChange={(e) => setBank({ ...bank, ifsc: e.target.value.toUpperCase().slice(0, 11) })}
                        />
                        <TextField fullWidth label="Bank name (optional)"
                          value={bank.bank_name}
                          onChange={(e) => setBank({ ...bank, bank_name: e.target.value })}
                        />
                      </Stack>
                      <Typography sx={{ fontSize: 11.5, color: 'var(--stone)', fontStyle: 'italic' }}>
                        Manual refunds are processed by our ops team within 2–3 working days.
                      </Typography>
                    </Stack>
                  </Collapse>
                </>
              )}

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ pt: 1 }}>
                <Button
                  fullWidth variant="outlined"
                  component={Link} to={`${ROUTES.TRACK}?id=${booking.booking_id}`}
                  sx={{ borderRadius: 100, textTransform: 'none', fontWeight: 600 }}
                >
                  Keep the booking
                </Button>
                <Button
                  fullWidth variant="contained"
                  onClick={onConfirm}
                  disabled={cancelling}
                  startIcon={cancelling ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : null}
                  sx={{
                    bgcolor: 'var(--red, #DC2626)', color: '#fff',
                    borderRadius: 100, textTransform: 'none', fontWeight: 700,
                    '&:hover': { bgcolor: '#B91C1C' },
                  }}
                >
                  {cancelling ? 'Cancelling…' : 'Confirm cancellation'}
                </Button>
              </Stack>
            </Stack>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default CancelBooking;
